import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { resizeImage } from '@troverecommerce/saas-components-ts';

interface ImageData {
  url: string;
  file?: File;
  key: string;
}

interface ImageUploadProps {
  images: Record<string, string>; // Current images { key: url }
  onImagesChange: (images: Record<string, string>, files: Record<string, File>, imageKeys: string[]) => void;
  maxFileSize?: number; // in MB
  disabled?: boolean;
  allowRename?: boolean; // Allow renaming image keys (default: true)
}

const MAX_FILE_SIZE_MB = 10;

/**
 * ImageUpload component for managing keyed images
 * Supports upload, rename, delete, and preview
 */
export const ImageUpload = ({
  images,
  onImagesChange,
  maxFileSize = MAX_FILE_SIZE_MB,
  disabled = false,
  allowRename = true,
}: ImageUploadProps) => {
  const [localImages, setLocalImages] = useState<Record<string, ImageData>>(() => {
    const initial: Record<string, ImageData> = {};
    Object.entries(images).forEach(([key, url]) => {
      initial[key] = { key, url };
    });
    return initial;
  });

  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [tempKeyName, setTempKeyName] = useState('');
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Update localImages when images prop changes (for loading existing images)
  useEffect(() => {
    const newLocalImages: Record<string, ImageData> = {};
    Object.entries(images).forEach(([key, url]) => {
      newLocalImages[key] = { key, url };
    });
    setLocalImages(newLocalImages);
  }, [images]);

  const notifyParent = useCallback(
    (updatedImages: Record<string, ImageData>, updatedFiles: Record<string, File>) => {
      const imageUrls: Record<string, string> = {};
      const imageKeys: string[] = [];

      Object.entries(updatedImages).forEach(([key, data]) => {
        imageUrls[key] = data.url;
        // imageKeys should include ALL keys we want to keep (existing + new)
        // The backend will:
        // - Keep existing images for keys in imageKeys that don't have files
        // - Upload new files for keys in imageKeys that have files
        // - Delete any images whose keys are NOT in imageKeys
        imageKeys.push(key);
      });

      onImagesChange(imageUrls, updatedFiles, imageKeys);
    },
    [onImagesChange]
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    handleFiles(Array.from(files));
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    handleFiles(Array.from(files));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFiles = (files: File[]) => {
    setUploadError(null);

    const newImages = { ...localImages };
    const newFiles = { ...pendingFiles };
    let hasError = false;

    files.forEach((file, index) => {
      // Validate file size
      if (file.size > maxFileSize * 1024 * 1024) {
        setUploadError(`File ${file.name} exceeds ${maxFileSize}MB limit`);
        hasError = true;
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError(`File ${file.name} is not an image`);
        hasError = true;
        return;
      }

      // Generate a unique key for the new image
      let key = index === 0 && !newImages.main ? 'main' : `alt${Object.keys(newImages).length}`;
      let counter = 1;
      while (newImages[key]) {
        key = `alt${counter}`;
        counter++;
      }

      // Create blob URL for preview
      const blobUrl = URL.createObjectURL(file);

      newImages[key] = {
        key,
        url: blobUrl,
        file,
      };

      newFiles[key] = file;
    });

    if (!hasError) {
      setLocalImages(newImages);
      setPendingFiles(newFiles);
      notifyParent(newImages, newFiles);
    }
  };

  const handleDeleteImage = (key: string) => {
    const newImages = { ...localImages };
    const newFiles = { ...pendingFiles };

    // Revoke blob URL if it exists
    if (newImages[key]?.file && newImages[key].url.startsWith('blob:')) {
      URL.revokeObjectURL(newImages[key].url);
    }

    delete newImages[key];
    delete newFiles[key];

    setLocalImages(newImages);
    setPendingFiles(newFiles);
    notifyParent(newImages, newFiles);
  };

  const handleStartRename = (key: string) => {
    setEditingKey(key);
    setTempKeyName(key);
  };

  const handleCancelRename = () => {
    setEditingKey(null);
    setTempKeyName('');
  };

  const handleSaveRename = async () => {
    if (!editingKey || !tempKeyName) return;

    // Validate key name (alphanumeric, dash, underscore only)
    if (!/^[a-zA-Z0-9_-]+$/.test(tempKeyName)) {
      setUploadError('Image key must contain only letters, numbers, dashes, and underscores');
      return;
    }

    // Check for duplicate key
    if (tempKeyName !== editingKey && localImages[tempKeyName]) {
      setUploadError('An image with this key already exists');
      return;
    }

    const newImages = { ...localImages };
    const newFiles = { ...pendingFiles };

    // Rename the image
    newImages[tempKeyName] = {
      ...newImages[editingKey],
      key: tempKeyName,
    };
    delete newImages[editingKey];

    // If this was a pending file, just rename it in the files map
    if (newFiles[editingKey]) {
      newFiles[tempKeyName] = newFiles[editingKey];
      delete newFiles[editingKey];
    } else {
      // For existing images, we need to fetch and convert to File so backend can re-upload with new key
      const imageUrl = localImages[editingKey].url;
      if (!imageUrl.startsWith('blob:')) {
        // It's an existing Cloudinary URL - fetch it and convert to File
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], `${tempKeyName}.jpg`, { type: blob.type });
          newFiles[tempKeyName] = file;
        } catch (error) {
          setUploadError('Failed to fetch existing image for rename');
          return;
        }
      }
    }

    setLocalImages(newImages);
    setPendingFiles(newFiles);
    notifyParent(newImages, newFiles);

    setEditingKey(null);
    setTempKeyName('');
    setUploadError(null);
  };

  // Sort images to ensure 'main' comes first, then alphabetically
  const imageEntries = Object.entries(localImages).sort(([keyA], [keyB]) => {
    if (keyA === 'main') return -1;
    if (keyB === 'main') return 1;
    return keyA.localeCompare(keyB);
  });

  return (
    <Box>
      {/* Upload Area */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          mb: 2,
          bgcolor: 'background.default',
          cursor: disabled ? 'not-allowed' : 'pointer',
          '&:hover': {
            borderColor: disabled ? 'divider' : 'primary.main',
            bgcolor: disabled ? 'background.default' : 'action.hover',
          },
        }}
      >
        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" gutterBottom>
          Drag and drop images here
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          or
        </Typography>
        <Button
          variant="contained"
          component="label"
          disabled={disabled}
          sx={{ mt: 1 }}
        >
          Choose Files
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            disabled={disabled}
          />
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          Maximum file size: 
          {' '}
          {maxFileSize}
          MB per image
        </Typography>
      </Box>

      {/* Error Display */}
      {uploadError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {uploadError}
        </Typography>
      )}

      {/* Image Gallery */}
      {imageEntries.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {imageEntries.map(([key, imageData]) => {
            // Use resizeImage for Cloudinary URLs, blob URLs as-is
            const displayUrl = imageData.url.startsWith('blob:') ?
              imageData.url :
              resizeImage(imageData.url, 800);

            return (
              <Card key={key} sx={{ width: 200 }}>
                <CardMedia
                  component="img"
                  height="150"
                  image={displayUrl}
                  alt={key}
                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setZoomImage(imageData.url)}
                />
                <CardActions sx={{ justifyContent: 'space-between', px: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {key}
                  </Typography>
                  <Box>
                    <IconButton
                    size="small"
                    onClick={() => setZoomImage(imageData.url)}
                    title="View full size"
                  >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                    {allowRename && (
                    <IconButton
                      size="small"
                      onClick={() => handleStartRename(key)}
                      disabled={disabled}
                      title="Rename key"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                    <IconButton
                    size="small"
                    onClick={() => handleDeleteImage(key)}
                    disabled={disabled}
                    color="error"
                    title="Delete image"
                  >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Rename Dialog */}
      <Dialog open={editingKey !== null} onClose={handleCancelRename}>
        <DialogTitle>Rename Image Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Image Key"
            fullWidth
            value={tempKeyName}
            onChange={e => setTempKeyName(e.target.value)}
            helperText="Use only letters, numbers, dashes, and underscores"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRename}>Cancel</Button>
          <Button onClick={handleSaveRename} variant="contained">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zoom Dialog */}
      <Dialog
        open={zoomImage !== null}
        onClose={() => setZoomImage(null)}
        maxWidth="lg"
      >
        <DialogContent>
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Zoomed"
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoomImage(null)}>Close</Button>
          <Button
            onClick={() => window.open(zoomImage || '', '_blank')}
            variant="contained"
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
